import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, Mail, Building2,
  RefreshCw, Download, X, ExternalLink, Calendar, Clock, Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LeadsScreen = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ teamSizes: [] });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [syncFilter, setSyncFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      const token = localStorage.getItem('d3_token');
      const params = new URLSearchParams({ limit: '200' });
      if (sourceFilter) params.set('source', sourceFilter);
      if (sizeFilter) params.set('team_size', sizeFilter);
      if (syncFilter) params.set('hubspot_status', syncFilter);
      if (search.trim()) params.set('search', search.trim());
      if (startDate) params.set('start_date', new Date(startDate).toISOString());
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        params.set('end_date', end.toISOString());
      }

      const res = await fetch(`${API_URL}/api/admin/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
        setTotal(data.total || 0);
        setStats(data.stats || {});
        setFilters(data.filters || { teamSizes: [] });
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
    setLoading(false);
    setRefreshing(false);
  }, [sourceFilter, sizeFilter, syncFilter, search, startDate, endDate]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLeads();
  };

  const clearFilters = () => {
    setSearch('');
    setSourceFilter('');
    setSizeFilter('');
    setSyncFilter('');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = sourceFilter || sizeFilter || syncFilter || startDate || endDate || search.trim();

  const exportLeadsCSV = () => {
    if (!leads.length) return;
    const headers = ['Name', 'Email', 'Company', 'Role', 'Team Size', 'Source', 'HubSpot Status', 'HubSpot Contact ID', 'HubSpot Deal ID', 'Date'];
    const rows = leads.map(l => [
      l.name || '', l.email || '', l.company || '', l.role || '',
      l.teamSize || '', l.formType === 'demo_request' ? 'Demo Request' : 'Email Capture',
      l.hubspotContactId ? 'Synced' : 'Not Synced',
      l.hubspotContactId || '', l.hubspotDealId || '',
      l.createdAt ? new Date(l.createdAt).toLocaleDateString() : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'D3_Command_Leads.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const statCards = [
    { label: 'Total Leads', value: stats.totalLeads || 0, color: 'from-blue-500 to-cyan-500' },
    { label: 'Demo Requests', value: stats.demoRequests || 0, color: 'from-purple-500 to-pink-500' },
    { label: 'Email Captures', value: stats.emailCaptures || 0, color: 'from-emerald-500 to-teal-500' },
    { label: 'HubSpot Synced', value: stats.hubspotSynced || 0, color: 'from-orange-500 to-amber-500' },
  ];

  return (
    <div className="space-y-6" data-testid="leads-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Lead Management</h1>
          <p className="text-gray-400 mt-1">View and manage all leads from your marketing channels</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 transition-colors text-sm"
            data-testid="refresh-leads-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={exportLeadsCSV}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium text-sm hover:opacity-90 transition-opacity"
            data-testid="export-leads-csv"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="p-5 rounded-2xl bg-white/5 border border-white/10"
          >
            <p className="text-sm text-gray-400 mb-1">{card.label}</p>
            <p className={`text-3xl font-bold bg-gradient-to-r ${card.color} bg-clip-text text-transparent`} data-testid={`lead-stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
              {card.value}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            data-testid="leads-search-input"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-blue-500/50"
            data-testid="leads-filter-source"
          >
            <option value="">All Sources</option>
            <option value="demo_request">Demo Requests</option>
            <option value="email_capture">Email Captures</option>
          </select>
          <select
            value={sizeFilter}
            onChange={(e) => setSizeFilter(e.target.value)}
            className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-blue-500/50"
            data-testid="leads-filter-size"
          >
            <option value="">All Sizes</option>
            {filters.teamSizes.map(ts => (
              <option key={ts} value={ts}>{ts}</option>
            ))}
          </select>
          <select
            value={syncFilter}
            onChange={(e) => setSyncFilter(e.target.value)}
            className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-blue-500/50"
            data-testid="leads-filter-hubspot"
          >
            <option value="">HubSpot: All</option>
            <option value="synced">Synced</option>
            <option value="not_synced">Not Synced</option>
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-blue-500/50"
            data-testid="leads-filter-start-date"
            title="From date"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-blue-500/50"
            data-testid="leads-filter-end-date"
            title="To date"
          />
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="h-10 px-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium hover:bg-red-500/20 transition-colors"
              data-testid="leads-clear-filters"
            >
              Clear
            </button>
          )}
        </div>
        <span className="text-xs text-gray-500 ml-auto">{total} result{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Leads Table */}
      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr className="text-left text-sm text-gray-400">
                <th className="px-5 py-4 font-medium">Lead</th>
                <th className="px-5 py-4 font-medium">Company</th>
                <th className="px-5 py-4 font-medium">Team Size</th>
                <th className="px-5 py-4 font-medium">Source</th>
                <th className="px-5 py-4 font-medium">HubSpot</th>
                <th className="px-5 py-4 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-500 text-sm">Loading leads...</td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-500 text-sm">
                    {hasActiveFilters ? 'No leads match your filters.' : 'No leads found. Leads will appear here when visitors submit forms on your marketing site.'}
                  </td>
                </tr>
              ) : (
                leads.map((lead) => {
                  const initials = lead.name ? lead.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : lead.email?.[0]?.toUpperCase() || '?';
                  const isDemo = lead.formType === 'demo_request';
                  const hasSynced = !!lead.hubspotContactId;

                  return (
                    <tr
                      key={lead.id}
                      className="border-t border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => setSelectedLead(lead)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDemo ? 'bg-purple-500/20' : 'bg-blue-500/20'}`}>
                            <span className={`text-sm font-bold ${isDemo ? 'text-purple-400' : 'text-blue-400'}`}>{initials}</span>
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{lead.name || 'Unknown'}</p>
                            <p className="text-gray-500 text-xs flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-gray-500" />
                          <span className="text-sm text-gray-300">{lead.company || '\u2014'}</span>
                        </div>
                        {lead.role && <p className="text-xs text-gray-500 mt-0.5">{lead.role}</p>}
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-gray-400">{lead.teamSize || '\u2014'}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          isDemo ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {isDemo ? 'Demo Request' : 'Email Capture'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {hasSynced ? (
                          <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            Synced
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                            <span className="w-2 h-2 rounded-full bg-gray-500" />
                            Not Synced
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-gray-400">
                          {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lead Detail Drawer */}
      <AnimatePresence>
        {selectedLead && (
          <div className="fixed inset-0 z-[60] flex justify-end" onClick={() => setSelectedLead(null)}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div
              initial={{ x: 420 }}
              animate={{ x: 0 }}
              exit={{ x: 420 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-md bg-[#0d1221] border-l border-white/10 shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="lead-detail-drawer"
            >
              {/* Drawer Header */}
              <div className="sticky top-0 bg-[#0d1221]/95 backdrop-blur-sm border-b border-white/10 px-6 py-5 flex items-center justify-between z-10">
                <h2 className="text-lg font-semibold text-white">Lead Details</h2>
                <button onClick={() => setSelectedLead(null)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="close-lead-drawer">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${selectedLead.formType === 'demo_request' ? 'bg-purple-500/20' : 'bg-blue-500/20'}`}>
                      <span className={`text-xl font-bold ${selectedLead.formType === 'demo_request' ? 'text-purple-400' : 'text-blue-400'}`}>
                        {selectedLead.name ? selectedLead.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg" data-testid="lead-detail-name">{selectedLead.name || 'Unknown'}</h3>
                      {selectedLead.role && <p className="text-sm text-gray-400">{selectedLead.role}</p>}
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="space-y-3">
                  <DetailRow icon={Mail} label="Email" value={selectedLead.email} />
                  <DetailRow icon={Building2} label="Company" value={selectedLead.company || '\u2014'} />
                  <DetailRow icon={Tag} label="Team Size" value={selectedLead.teamSize || '\u2014'} />
                  <DetailRow icon={Tag} label="Source" value={
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      selectedLead.formType === 'demo_request' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {selectedLead.formType === 'demo_request' ? 'Demo Request' : 'Email Capture'}
                    </span>
                  } />
                  <DetailRow icon={Calendar} label="Created" value={
                    selectedLead.createdAt ? new Date(selectedLead.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '\u2014'
                  } />
                  {selectedLead.message && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">Message</p>
                      <p className="text-sm text-gray-300 bg-white/5 rounded-xl p-3 border border-white/10">{selectedLead.message}</p>
                    </div>
                  )}
                </div>

                {/* HubSpot Section */}
                <div className="pt-2">
                  <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-orange-400" />
                    HubSpot Integration
                  </h4>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Sync Status</span>
                      {selectedLead.hubspotContactId ? (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400">
                          <span className="w-2 h-2 rounded-full bg-emerald-400" />
                          Synced
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                          <span className="w-2 h-2 rounded-full bg-gray-500" />
                          Not Synced
                        </span>
                      )}
                    </div>
                    {selectedLead.hubspotContactId && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Contact ID</span>
                          <span className="text-sm text-white font-mono">{selectedLead.hubspotContactId}</span>
                        </div>
                        <a
                          href={`https://app.hubspot.com/contacts/search?query=${encodeURIComponent(selectedLead.email)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 w-full py-2.5 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium justify-center hover:bg-orange-500/20 transition-colors"
                          data-testid="hubspot-link"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open in HubSpot
                        </a>
                      </>
                    )}
                    {selectedLead.hubspotDealId && (
                      <div className="flex items-center justify-between pt-1 border-t border-white/5">
                        <span className="text-sm text-gray-400">Deal ID</span>
                        <span className="text-sm text-white font-mono">{selectedLead.hubspotDealId}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamps */}
                <div className="pt-2">
                  <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    Timeline
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-blue-400" />
                      <span className="text-gray-400">Created:</span>
                      <span className="text-white">
                        {selectedLead.createdAt ? new Date(selectedLead.createdAt).toLocaleString() : '\u2014'}
                      </span>
                    </div>
                    {selectedLead.hubspotContactId && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-orange-400" />
                        <span className="text-gray-400">HubSpot synced</span>
                      </div>
                    )}
                    {selectedLead.hubspotDealId && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-gray-400">Deal created in HubSpot</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5">
      <span className="text-sm text-gray-400 flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {label}
      </span>
      <span className="text-sm text-white">{value}</span>
    </div>
  );
}
